import OrderUserApiAgency from '@/api/OrderUserApiAgency';
import type OrderEntity from '@/entity/order/OrderEntity';
import { i18n } from '@/i18n';
import router from '@/router/index.router';
import { Reactive } from '@/utils/decorators';
import { Bus, useAside, useLoader, useModal } from '@orion.ui/orion';
import { pick } from 'lodash-es';
import { defineAsyncComponent } from 'vue';
import useBusService from './BusService';

type AsideNestProps = {
	id: number
	order?: OrderEntity
	orderClientProposal?: IOrderClientProposalWithUserModel
	tab?: string
	highlightElement?: string
	isValidatedCandidate?: Boolean
};

type CandidateRating = {
	candidateId: number
	numberOfRating: number
	rating: number
};

type CandidateTalents = {
	candidateId: number
	talents: ITalentModel[]
};

class CandidateService {

	@Reactive private readonly state = {
		ratingList: [] as CandidateRating[],
		talentsList: [] as CandidateTalents[],
	};

	get ratingList () { return this.state.ratingList }
	get talentsList () { return this.state.talentsList }

	getCandidateRating (candidateId: number): Undef<CandidateRating> {
		return this.state.ratingList.findByKey(candidateId, 'candidateId');
	}

	setCandidateRating (payload: CandidateRating) {
		const targetCandidate = this.getCandidateRating(payload.candidateId);

		if (targetCandidate) {
			Object.assign(targetCandidate, pick(payload, ['numberOfRating', 'rating']));
		}
		else {
			this.state.ratingList.push(pick(payload, ['candidateId', 'numberOfRating', 'rating']));
		}

		useBusService().emit(`listItem:rankingUpdate:${payload.candidateId}`, payload.rating);
	}

	getCandidateTalents (candidateId: number) {
		return this.state.talentsList.findByKey(candidateId, 'candidateId')?.talents ?? [];
	}

	setCandidateTalents (payload: CandidateTalents) {
		const targetCandidate = this.state.talentsList.findByKey(payload.candidateId, 'candidateId');
		if (targetCandidate) {
			targetCandidate.talents = payload.talents ?? [];
		}
		else {
			this.state.talentsList.push(pick(payload, ['candidateId', 'talents']));
		}
	}

	async openAsideProfileAsync (nestedProps: AsideNestProps) {
		useAside({
			Nested: defineAsyncComponent(() => import('@/components/temp-worker/TempWorkerAsideDetails.vue')),
			NestedProps: nestedProps,
			size: 'lg',
		});
	}

	setCandidateData (candidateData: CandidateRating & CandidateTalents) {
		this.setCandidateRating(candidateData);
		this.setCandidateTalents(candidateData);
	}

	async addCandidatesAsync (order: OrderEntity) {
		useModal({
			Nested: defineAsyncComponent(() => import('@/components/order/OrderModalCandidate.vue')),
			NestedProps: {
				orderId: Number(router.currentRoute.value.params.id),
				order: order,
			},
			customClass: 'order-modal-candidate',
			size: 'xl',
			events: {
				'addCandidates': async (Modal, payload) => {
					try {
						useLoader().show(i18n.global.t('common.addingSelectedCandidates'));

						await OrderUserApiAgency.add({
							userIds: (payload as Armado.TempWorkerListItemEntityType[]).map(x => x.id),
							orderId: order.id,
						});

						await Modal.close();

						if (this.inOrder()) {
							Bus.emit('OrderDetailsPipelineSetup:added');
							Bus.emit('OrderDetailsSetup:refreshEntity');
						}
						else {
							Bus.emit('TempWorkerPagedListViewSetup:added');
						}
					}
					finally {
						useLoader().hide();
					}
				},
			},
		});
	}

	async rejectAsync (...args: Parameters<typeof OrderUserApiAgency.reject>) {
		const { data } = await OrderUserApiAgency.reject(...args);
		return data;
	}

	async rejectCancelAsync (...args: Parameters<typeof OrderUserApiAgency.rejectCancel>) {
		const { data } = await OrderUserApiAgency.rejectCancel(...args);
		return data;
	}

	inOrder () {
		return router.currentRoute.value.name === 'OrderDetailsPipeline';
	}

	inTalentPools () {
		return router.currentRoute.value.name === 'ToolboxTalentPool';
	}

	inCandidates () {
		return router.currentRoute.value.name === 'TempWorker';
	}

}

const candidateServiceSingleton = new CandidateService();

export default function useCandidateService () {
	return candidateServiceSingleton;
}
